import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { offersAPI, customersAPI } from '../../services/api';
import { useCompany } from '../../services/companyState';
import {
  SERVICE_CATEGORIES,
  UNITS,
  findServiceByKey,
  unitLabel,
  formatEuro,
} from '../../services/serviceCatalog';
import LoadingSpinner from '../Common/LoadingSpinner';
import './OfferForm.css';

const EMPTY_FORM = {
  customer_id: '',
  offer_date: new Date().toISOString().slice(0, 10),
  valid_until: '',
  title: '',
  notes: '',
};

const parseNumber = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const lineNet = (item) => parseNumber(item.quantity) * parseNumber(item.unit_price);
const lineVat = (item) => lineNet(item) * (parseNumber(item.vat_rate) / 100);
const lineGross = (item) => lineNet(item) + lineVat(item);
const lineTotal = lineNet; // legacy alias

const VAT_RATES = ['0', '7', '19'];

const makeItemFromService = (service, position, defaultVatRate) => ({
  service_key: service.key,
  description: service.name,
  quantity: '1',
  unit_price: String(service.default_price),
  unit: service.unit,
  vat_rate: String(defaultVatRate ?? 0),
  position,
});

const makeCustomItem = (position, defaultVatRate) => ({
  service_key: null,
  description: '',
  quantity: '1',
  unit_price: '0',
  unit: 'pauschal',
  vat_rate: String(defaultVatRate ?? 0),
  position,
});

const OfferForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const company = useCompany();
  const isKleinunternehmer = company?.small_business === true;
  const defaultVatRate = isKleinunternehmer ? 0 : Number(company?.vat_default_rate ?? 19);

  const [form, setForm] = useState(EMPTY_FORM);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const customerResp = await customersAPI.getAll({ active_only: false, limit: 500 });
        if (cancelled) return;
        setCustomers(customerResp.data || []);

        if (isEdit) {
          const { data } = await offersAPI.getById(id);
          if (cancelled) return;
          setForm({
            customer_id: String(data.customer_id || ''),
            offer_date: data.offer_date || EMPTY_FORM.offer_date,
            valid_until: data.valid_until || '',
            title: data.title || '',
            notes: data.notes || '',
          });
          setItems(
            (data.items || [])
              .slice()
              .sort((a, b) => (a.position || 0) - (b.position || 0))
              .map((it, idx) => ({
                service_key: it.service_key || null,
                description: it.description || '',
                quantity: String(it.quantity ?? '1'),
                unit_price: String(it.unit_price ?? '0'),
                unit: it.unit || 'pauschal',
                vat_rate: String(it.vat_rate ?? 0),
                position: idx,
              }))
          );
        }
      } catch (err) {
        toast.error(
          err.response?.status === 404
            ? 'Angebot nicht gefunden'
            : 'Fehler beim Laden'
        );
        if (isEdit) navigate('/offers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  const totals = useMemo(() => {
    let net = 0;
    let vat = 0;
    const byRate = {};
    for (const it of items) {
      const n = lineNet(it);
      const r = parseNumber(it.vat_rate);
      const v = n * (r / 100);
      net += n;
      vat += v;
      const key = r.toFixed(2);
      if (!byRate[key]) byRate[key] = { rate: r, net: 0, vat: 0 };
      byRate[key].net += n;
      byRate[key].vat += v;
    }
    return {
      net,
      vat,
      gross: net + vat,
      breakdown: Object.values(byRate).sort((a, b) => a.rate - b.rate),
    };
  }, [items]);
  const total = useMemo(
    () => items.reduce((sum, it) => sum + lineTotal(it), 0),
    [items]
  );

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const addServiceFromCatalog = (service) => {
    setItems((prev) => [...prev, makeItemFromService(service, prev.length, defaultVatRate)]);
  };

  const addCustomItem = () => {
    setItems((prev) => [...prev, makeCustomItem(prev.length, defaultVatRate)]);
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it))
    );
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const filteredCategories = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    if (!q) return SERVICE_CATEGORIES;
    return SERVICE_CATEGORIES.map((cat) => ({
      ...cat,
      services: cat.services.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          (cat.name || '').toLowerCase().includes(q)
      ),
    })).filter((cat) => cat.services.length > 0);
  }, [catalogSearch]);

  const validate = () => {
    const next = {};
    if (!form.customer_id) next.customer_id = 'Bitte Kunde auswählen';
    if (!form.offer_date) next.offer_date = 'Angebotsdatum erforderlich';
    if (items.length === 0) next.items = 'Mindestens eine Leistung hinzufügen';
    items.forEach((it, idx) => {
      if (!it.description.trim()) next[`item_${idx}_description`] = 'Beschreibung fehlt';
      if (parseNumber(it.quantity) <= 0) next[`item_${idx}_quantity`] = 'Menge > 0';
      if (parseNumber(it.unit_price) < 0) next[`item_${idx}_unit_price`] = 'Preis ≥ 0';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Bitte Eingaben prüfen');
      return;
    }
    setSaving(true);
    const payload = {
      customer_id: Number(form.customer_id),
      offer_date: form.offer_date,
      valid_until: form.valid_until || null,
      title: form.title || null,
      notes: form.notes || null,
      items: items.map((it, idx) => ({
        service_key: it.service_key || null,
        description: it.description,
        quantity: parseNumber(it.quantity),
        unit_price: parseNumber(it.unit_price),
        unit: it.unit,
        vat_rate: isKleinunternehmer ? 0 : parseNumber(it.vat_rate),
        position: idx,
      })),
    };
    try {
      if (isEdit) {
        await offersAPI.update(id, payload);
        toast.success('Angebot aktualisiert');
      } else {
        await offersAPI.create(payload);
        toast.success('Angebot erstellt');
      }
      navigate('/offers');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const map = {};
        detail.forEach((d) => {
          const field = d.loc?.slice(-1)?.[0];
          if (field) map[field] = d.msg;
        });
        setErrors(map);
      }
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Fehler beim Speichern'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <div>
          <Link to="/offers" className="back-link">
            <ArrowLeft size={14} />
            Zurück zur Übersicht
          </Link>
          <h1>{isEdit ? 'Angebot bearbeiten' : 'Neues Angebot'}</h1>
          <p>Leistungen aus dem Katalog wählen oder eigene hinzufügen</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="content-body offer-form-grid">
        {/* Header info */}
        <div className="card">
          <div className="card-header"><h2>Angebotsdaten</h2></div>
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="customer_id">Kunde *</label>
                <select
                  id="customer_id"
                  name="customer_id"
                  value={form.customer_id}
                  onChange={handleField}
                  className={`form-input ${errors.customer_id ? 'is-invalid' : ''}`}
                  disabled={saving}
                >
                  <option value="">-- Kunde wählen --</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.city ? `(${c.city})` : ''}
                    </option>
                  ))}
                </select>
                {errors.customer_id && (
                  <div className="field-error">{errors.customer_id}</div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="title">Titel</label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  value={form.title}
                  onChange={handleField}
                  placeholder="z.B. Webseitenerstellung Firma Müller"
                  className="form-input"
                  disabled={saving}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="offer_date">Angebotsdatum *</label>
                <input
                  id="offer_date"
                  name="offer_date"
                  type="date"
                  value={form.offer_date}
                  onChange={handleField}
                  className={`form-input ${errors.offer_date ? 'is-invalid' : ''}`}
                  disabled={saving}
                />
                {errors.offer_date && (
                  <div className="field-error">{errors.offer_date}</div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="valid_until">Gültig bis</label>
                <input
                  id="valid_until"
                  name="valid_until"
                  type="date"
                  value={form.valid_until}
                  onChange={handleField}
                  className="form-input"
                  disabled={saving}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Service Catalog picker */}
        <div className="card">
          <div className="card-header">
            <h2>Leistungskatalog</h2>
          </div>
          <div className="form-section">
            <div className="catalog-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Leistung suchen..."
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                className="form-input"
                disabled={saving}
              />
            </div>
            <div className="catalog-categories">
              {filteredCategories.map((cat) => (
                <div key={cat.id} className="catalog-category">
                  <h3>{cat.name}</h3>
                  <div className="catalog-services">
                    {cat.services.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        className="catalog-service-btn"
                        onClick={() => addServiceFromCatalog(s)}
                        disabled={saving}
                      >
                        <div className="catalog-service-name">{s.name}</div>
                        <div className="catalog-service-meta">
                          {formatEuro(s.default_price)} · {unitLabel(s.unit)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addCustomItem}
              className="btn btn-outline"
              disabled={saving}
            >
              <Plus size={14} />
              Eigene Leistung hinzufügen
            </button>
          </div>
        </div>

        {/* Line items */}
        <div className="card">
          <div className="card-header">
            <h2>Positionen</h2>
          </div>
          <div className="form-section">
            {items.length === 0 ? (
              <div className="empty-items">
                Noch keine Leistungen ausgewählt. Wähle eine aus dem Katalog oder füge eine eigene hinzu.
              </div>
            ) : (
              <div className="line-items">
                {items.map((it, idx) => {
                  const catalog = findServiceByKey(it.service_key);
                  return (
                    <div key={idx} className="line-item">
                      <div className="line-item-grid">
                        <div className="li-field li-description">
                          <label className="form-label">Beschreibung</label>
                          <input
                            type="text"
                            value={it.description}
                            onChange={(e) =>
                              updateItem(idx, 'description', e.target.value)
                            }
                            className={`form-input ${
                              errors[`item_${idx}_description`] ? 'is-invalid' : ''
                            }`}
                            disabled={saving}
                          />
                          {catalog && (
                            <div className="li-catalog-tag">
                              Katalog: {catalog.categoryName}
                            </div>
                          )}
                        </div>
                        <div className="li-field li-qty">
                          <label className="form-label">Menge</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={it.quantity}
                            onChange={(e) =>
                              updateItem(idx, 'quantity', e.target.value)
                            }
                            className={`form-input ${
                              errors[`item_${idx}_quantity`] ? 'is-invalid' : ''
                            }`}
                            disabled={saving}
                          />
                        </div>
                        <div className="li-field li-price">
                          <label className="form-label">Einzelpreis (€)</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={it.unit_price}
                            onChange={(e) =>
                              updateItem(idx, 'unit_price', e.target.value)
                            }
                            className={`form-input ${
                              errors[`item_${idx}_unit_price`] ? 'is-invalid' : ''
                            }`}
                            disabled={saving}
                          />
                        </div>
                        <div className="li-field li-unit">
                          <label className="form-label">Einheit</label>
                          <select
                            value={it.unit}
                            onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                            className="form-input"
                            disabled={saving}
                          >
                            {UNITS.map((u) => (
                              <option key={u.value} value={u.value}>
                                {u.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {!isKleinunternehmer && (
                          <div className="li-field li-vat" style={{ minWidth: 90 }}>
                            <label className="form-label">USt %</label>
                            <select
                              value={it.vat_rate}
                              onChange={(e) => updateItem(idx, 'vat_rate', e.target.value)}
                              className="form-input"
                              disabled={saving}
                            >
                              {VAT_RATES.map((r) => (
                                <option key={r} value={r}>{r} %</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="li-field li-total">
                          <label className="form-label">Gesamt</label>
                          <div className="li-total-value">
                            {formatEuro(lineGross(it))}
                            {!isKleinunternehmer && parseNumber(it.vat_rate) > 0 && (
                              <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 400 }}>
                                (netto {formatEuro(lineNet(it))})
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="li-field li-actions">
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => removeItem(idx)}
                            disabled={saving}
                            title="Position entfernen"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {errors.items && <div className="field-error">{errors.items}</div>}

            <div className="offer-total">
              {isKleinunternehmer ? (
                <>
                  <div className="offer-total-row">
                    <span>Gesamtbetrag</span>
                    <span className="offer-total-value">{formatEuro(totals.net)}</span>
                  </div>
                  <div className="kleinunternehmer-notice">
                    Gemäß §19 UStG wird keine Umsatzsteuer berechnet.
                  </div>
                </>
              ) : (
                <>
                  <div className="offer-total-row" style={{ fontSize: '0.95rem' }}>
                    <span>Nettobetrag</span>
                    <span>{formatEuro(totals.net)}</span>
                  </div>
                  {totals.breakdown
                    .filter((b) => b.rate > 0)
                    .map((b) => (
                      <div key={b.rate} className="offer-total-row" style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                        <span>USt {b.rate.toFixed(0)}% (auf {formatEuro(b.net)})</span>
                        <span>{formatEuro(b.vat)}</span>
                      </div>
                    ))}
                  {totals.breakdown.some((b) => b.rate === 0) && (
                    <div className="offer-total-row" style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      <span>Steuerfrei (auf {formatEuro(totals.breakdown.find((b) => b.rate === 0).net)})</span>
                      <span>0,00 €</span>
                    </div>
                  )}
                  <div className="offer-total-row" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.5rem', marginTop: '0.4rem' }}>
                    <span>Gesamtbetrag (brutto)</span>
                    <span className="offer-total-value">{formatEuro(totals.gross)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="card">
          <div className="card-header"><h2>Bemerkungen</h2></div>
          <div className="form-section">
            <div className="form-group">
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleField}
                className="form-input"
                rows={4}
                placeholder="Interne Notizen oder Bemerkungen für den Kunden"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <Link to="/offers" className="btn btn-outline" aria-disabled={saving}>
            Abbrechen
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <Save size={14} />
            {saving ? 'Speichern...' : isEdit ? 'Änderungen speichern' : 'Angebot erstellen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OfferForm;
